import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import processFlowService from '../services/processFlowService';

const useProcessFlowStore = create(
  persist(
    (set, get) => ({
      // State
      processFlows: [],
      currentFlow: null,
      templates: [],
      sharedFlows: [],
      isLoading: false,
      isAutoSaving: false,
      error: null,
      filters: {
        category: '',
        status: '',
        search: '',
        isTemplate: false
      },
      pagination: {
        page: 1,
        limit: 20,
        total: 0,
        totalPages: 0
      },

      // Auto-save state
      autoSaveTimeout: null,
      lastAutoSaveAt: null,
      hasUnsavedChanges: false,

      // Actions
      setLoading: (isLoading) => set({ isLoading }),
      setError: (error) => set({ error }),
      setFilters: (filters) => set((state) => ({
        filters: { ...state.filters, ...filters },
        pagination: { ...state.pagination, page: 1 } // Reset page on filter change
      })),
      setPagination: (pagination) => set((state) => ({
        pagination: { ...state.pagination, ...pagination }
      })),

      // Fetch process flows
      fetchProcessFlows: async (options = {}) => {
        set({ isLoading: true, error: null });
        try {
          const { filters, pagination } = get();
          const params = {
            ...filters,
            ...pagination,
            ...options
          };

          const response = await processFlowService.getProcessFlows(params);
          console.log('ProcessFlow API Response:', response);

          // Handle both direct array response and data object response
          const flows = Array.isArray(response) ? response : (response.data?.processFlows || response.processFlows || []);
          const meta = response.meta || response.pagination || {};

          set({
            processFlows: flows,
            pagination: meta,
            isLoading: false
          });
          return flows;
        } catch (error) {
          console.error('Failed to fetch process flows:', error);
          set({ error: error.message, isLoading: false, processFlows: [] });
          throw error;
        }
      },

      // Fetch single process flow
      fetchProcessFlow: async (id) => {
        set({ isLoading: true, error: null });
        try {
          const flow = await processFlowService.getProcessFlow(id);
          set({ currentFlow: flow, isLoading: false, hasUnsavedChanges: false });
          return flow;
        } catch (error) {
          set({ error: error.message, isLoading: false });
          throw error;
        }
      },

      // Create process flow
      createProcessFlow: async (data) => {
        set({ isLoading: true, error: null });
        try {
          const flow = await processFlowService.createProcessFlow(data);
          set((state) => ({
            processFlows: [flow, ...state.processFlows],
            currentFlow: flow,
            isLoading: false
          }));
          return flow;
        } catch (error) {
          set({ error: error.message, isLoading: false });
          throw error;
        }
      },

      // Update process flow
      updateProcessFlow: async (id, data) => {
        set({ isLoading: true, error: null });
        try {
          const flow = await processFlowService.updateProcessFlow(id, data);
          set((state) => ({
            processFlows: state.processFlows.map(f =>
              f._id === id ? flow : f
            ),
            currentFlow: state.currentFlow?._id === id ? flow : state.currentFlow,
            isLoading: false,
            hasUnsavedChanges: false
          }));
          return flow;
        } catch (error) {
          set({ error: error.message, isLoading: false });
          throw error;
        }
      },

      // Delete process flow
      deleteProcessFlow: async (id) => {
        set({ isLoading: true, error: null });
        try {
          await processFlowService.deleteProcessFlow(id);
          set((state) => ({
            processFlows: state.processFlows.filter(f => f._id !== id),
            currentFlow: state.currentFlow?._id === id ? null : state.currentFlow,
            isLoading: false
          }));
        } catch (error) {
          set({ error: error.message, isLoading: false });
          throw error;
        }
      },

      // Update current flow locally (for editor changes)
      updateCurrentFlow: (updates) => {
        set((state) => ({
          currentFlow: state.currentFlow ? {
            ...state.currentFlow,
            ...updates
          } : null,
          hasUnsavedChanges: true
        }));

        // Trigger auto-save
        get().triggerAutoSave();
      },

      // Auto-save functionality
      triggerAutoSave: () => {
        const { autoSaveTimeout, currentFlow } = get();

        // Clear existing timeout
        if (autoSaveTimeout) {
          clearTimeout(autoSaveTimeout);
        }

        // Set new timeout for auto-save (3 seconds)
        const newTimeout = setTimeout(() => {
          get().autoSave();
        }, 3000);

        set({ autoSaveTimeout: newTimeout });
      },

      autoSave: async () => {
        const { currentFlow, hasUnsavedChanges } = get();

        // Don't auto-save if no flow, no changes, or no flow ID (in create mode)
        if (!currentFlow || !hasUnsavedChanges || !currentFlow._id) return;

        set({ isAutoSaving: true });
        try {
          const result = await processFlowService.autoSaveProcessFlow(
            currentFlow._id,
            {
              nodes: currentFlow.nodes,
              edges: currentFlow.edges,
              viewport: currentFlow.viewport
            }
          );
          set({
            isAutoSaving: false,
            lastAutoSaveAt: result.savedAt,
            hasUnsavedChanges: false
          });
        } catch (error) {
          set({ isAutoSaving: false });
          console.error('Auto-save failed:', error);
        }
      },

      // Share process flow
      shareProcessFlow: async (id, shareData) => {
        set({ isLoading: true, error: null });
        try {
          const flow = await processFlowService.shareProcessFlow(id, shareData);
          set((state) => ({
            processFlows: state.processFlows.map(f =>
              f._id === id ? flow : f
            ),
            currentFlow: state.currentFlow?._id === id ? flow : state.currentFlow,
            isLoading: false
          }));
          return flow;
        } catch (error) {
          set({ error: error.message, isLoading: false });
          throw error;
        }
      },

      // Unshare process flow
      unshareProcessFlow: async (id, userId) => {
        set({ isLoading: true, error: null });
        try {
          const flow = await processFlowService.unshareProcessFlow(id, userId);
          set((state) => ({
            processFlows: state.processFlows.map(f =>
              f._id === id ? flow : f
            ),
            currentFlow: state.currentFlow?._id === id ? flow : state.currentFlow,
            isLoading: false
          }));
          return flow;
        } catch (error) {
          set({ error: error.message, isLoading: false });
          throw error;
        }
      },

      // Add comment
      addComment: async (id, commentData) => {
        try {
          const flow = await processFlowService.addComment(id, commentData);
          set((state) => ({
            currentFlow: state.currentFlow?._id === id ? flow : state.currentFlow
          }));
          return flow;
        } catch (error) {
          set({ error: error.message });
          throw error;
        }
      },

      // Resolve comment
      resolveComment: async (id, commentId) => {
        try {
          const flow = await processFlowService.resolveComment(id, commentId);
          set((state) => ({
            currentFlow: state.currentFlow?._id === id ? flow : state.currentFlow
          }));
          return flow;
        } catch (error) {
          set({ error: error.message });
          throw error;
        }
      },

      // Duplicate process flow
      duplicateProcessFlow: async (id, data) => {
        set({ isLoading: true, error: null });
        try {
          const flow = await processFlowService.duplicateProcessFlow(id, data);
          set((state) => ({
            processFlows: [flow, ...state.processFlows],
            isLoading: false
          }));
          return flow;
        } catch (error) {
          set({ error: error.message, isLoading: false });
          throw error;
        }
      },

      // Fetch templates
      fetchTemplates: async (category) => {
        set({ isLoading: true, error: null });
        try {
          const templates = await processFlowService.getTemplates(category);
          set({ templates, isLoading: false });
          return templates;
        } catch (error) {
          set({ error: error.message, isLoading: false });
          throw error;
        }
      },

      // Create from template
      createFromTemplate: async (templateId, data) => {
        set({ isLoading: true, error: null });
        try {
          const flow = await processFlowService.createFromTemplate(templateId, data);
          set((state) => ({
            processFlows: [flow, ...state.processFlows],
            currentFlow: flow,
            isLoading: false
          }));
          return flow;
        } catch (error) {
          set({ error: error.message, isLoading: false });
          throw error;
        }
      },

      // Convert to template
      convertToTemplate: async (id, category) => {
        set({ isLoading: true, error: null });
        try {
          const flow = await processFlowService.convertToTemplate(id, category);
          set((state) => ({
            processFlows: state.processFlows.map(f =>
              f._id === id ? flow : f
            ),
            currentFlow: state.currentFlow?._id === id ? flow : state.currentFlow,
            isLoading: false
          }));
          return flow;
        } catch (error) {
          set({ error: error.message, isLoading: false });
          throw error;
        }
      },

      // Export process flow
      exportProcessFlow: async (id, format = 'json') => {
        try {
          const data = await processFlowService.exportProcessFlow(id, format);
          return data;
        } catch (error) {
          set({ error: error.message });
          throw error;
        }
      },

      // Import process flow
      importProcessFlow: async (data) => {
        set({ isLoading: true, error: null });
        try {
          const flow = await processFlowService.importProcessFlow(data);
          set((state) => ({
            processFlows: [flow, ...state.processFlows],
            currentFlow: flow,
            isLoading: false
          }));
          return flow;
        } catch (error) {
          set({ error: error.message, isLoading: false });
          throw error;
        }
      },

      // Get versions
      getVersions: async (id) => {
        try {
          const versions = await processFlowService.getVersions(id);
          return versions;
        } catch (error) {
          set({ error: error.message });
          throw error;
        }
      },

      // Restore version
      restoreVersion: async (id, versionNumber) => {
        set({ isLoading: true, error: null });
        try {
          const flow = await processFlowService.restoreVersion(id, versionNumber);
          set((state) => ({
            currentFlow: state.currentFlow?._id === id ? flow : state.currentFlow,
            isLoading: false,
            hasUnsavedChanges: false
          }));
          return flow;
        } catch (error) {
          set({ error: error.message, isLoading: false });
          throw error;
        }
      },

      // Fetch shared with me
      fetchSharedWithMe: async () => {
        set({ isLoading: true, error: null });
        try {
          const response = await processFlowService.getSharedWithMe();
          set({
            sharedFlows: response.data?.processFlows || [],
            isLoading: false
          });
          return response.data?.processFlows || [];
        } catch (error) {
          set({ error: error.message, isLoading: false });
          throw error;
        }
      },

      // Fetch my flows
      fetchMyFlows: async () => {
        set({ isLoading: true, error: null });
        try {
          const response = await processFlowService.getMyProcessFlows();
          set({
            processFlows: response.data?.processFlows || [],
            pagination: response.meta || {},
            isLoading: false
          });
          return response.data?.processFlows || [];
        } catch (error) {
          set({ error: error.message, isLoading: false });
          throw error;
        }
      },

      // Initialize empty flow for create mode
      initializeEmptyFlow: () => {
        const emptyFlow = {
          _id: null, // No ID until saved
          name: 'New Process Flow',
          description: '',
          category: 'process',
          status: 'draft',
          nodes: [],
          edges: [],
          viewport: { x: 0, y: 0, zoom: 1 },
          isTemplate: false,
          isPublic: false,
          shares: [],
          comments: []
        };

        set({
          currentFlow: emptyFlow,
          hasUnsavedChanges: false,
          lastAutoSaveAt: null
        });
      },

      // Clear current flow
      clearCurrentFlow: () => {
        const { autoSaveTimeout } = get();
        if (autoSaveTimeout) {
          clearTimeout(autoSaveTimeout);
        }
        set({
          currentFlow: null,
          hasUnsavedChanges: false,
          autoSaveTimeout: null,
          lastAutoSaveAt: null
        });
      },

      // Reset store
      resetStore: () => {
        const { autoSaveTimeout } = get();
        if (autoSaveTimeout) {
          clearTimeout(autoSaveTimeout);
        }
        set({
          processFlows: [],
          currentFlow: null,
          templates: [],
          sharedFlows: [],
          isLoading: false,
          isAutoSaving: false,
          error: null,
          filters: {
            category: '',
            status: '',
            search: '',
            isTemplate: false
          },
          pagination: {
            page: 1,
            limit: 20,
            total: 0,
            totalPages: 0
          },
          autoSaveTimeout: null,
          lastAutoSaveAt: null,
          hasUnsavedChanges: false
        });
      }
    }),
    {
      name: 'process-flow-storage',
      partialize: (state) => ({
        filters: state.filters,
        pagination: { page: state.pagination.page, limit: state.pagination.limit }
      })
    }
  )
);

export default useProcessFlowStore;